import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SaveIcon from "@mui/icons-material/Save";
import { listSettings, updateSetting } from "../../api/settings";

export default function SettingsAdmin() {
  const [rows, setRows] = useState([]);
  const [drafts, setDrafts] = useState({}); // settingKey -> string
  const [loading, setLoading] = useState(false);
  const [savingKey, setSavingKey] = useState(null);
  const [error, setError] = useState("");

  const fetchAll = useCallback(() => {
    setLoading(true);
    setError("");
    listSettings()
      .then((d) => {
        setRows(Array.isArray(d) ? d : []);
        setDrafts({});
      })
      .catch((e) => setError(e.message || "Failed to load settings"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const save = async (row) => {
    const value = drafts[row.settingKey];
    if (value === undefined || value === row.settingValue) return;
    try {
      setSavingKey(row.settingKey);
      await updateSetting(row.settingKey, { settingValue: value });
      fetchAll();
    } catch (e) {
      setError(e.message || "Save failed");
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>Settings</Typography>
        <Button startIcon={<RefreshIcon />} onClick={fetchAll}>Refresh</Button>
      </Stack>

      {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Key</TableCell>
              <TableCell>Value</TableCell>
              <TableCell>Description</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={4} align="center"><CircularProgress size={24} /></TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={4} align="center">No settings</TableCell></TableRow>
            ) : rows.map((row) => {
              const draft = drafts[row.settingKey];
              const dirty = draft !== undefined && draft !== row.settingValue;
              return (
                <TableRow key={row.settingKey}>
                  <TableCell sx={{ fontFamily: "monospace" }}>{row.settingKey}</TableCell>
                  <TableCell sx={{ minWidth: 200 }}>
                    <TextField
                      size="small" fullWidth
                      value={draft !== undefined ? draft : row.settingValue}
                      onChange={(e) => setDrafts({ ...drafts, [row.settingKey]: e.target.value })}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">{row.description || ""}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => save(row)}
                      disabled={!dirty || savingKey === row.settingKey}
                      color={dirty ? "primary" : "default"}
                    >
                      <SaveIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
